import React, { useEffect, useState } from 'react';
import { Button, Dialog, DialogContent, IconButton, Close, makeStyles } from '@quillbot/design-system';
import QuillLocalStorage from 'core/utils/localStorageClient';
import { useRouter } from 'next/router';
import { useSelector } from 'react-redux';
import { RootState } from '@Common/models/redux/store';
import { theme } from 'config/theme';
import Link from 'next/link';
import logger from 'core/logger/logger';
import Track from '@Common/handlers/track';
import Img from '@Common/components/img';
import { firebase } from 'config/firebase';

const useStyles = makeStyles(() => ({
  dialogPaper: {
    width: 320,
    borderRadius: 8,
  },
  dialogContent: {
    padding: 0,
    backgroundColor: theme.specialColors.WHITE_TEXT,
  },
  discountWrapper: {
    backgroundColor: theme.palette.primary.main,
    textAlign: 'center',
    padding: '32px 24px 22px',
  },
  topText: {
    fontSize: '19px',
    color: theme.specialColors.WHITE_TEXT,
  },
  couponImg: {
    height: 68,
    marginTop: 12,
    marginBottom: 8,
  },
  discountLabel: {
    color: theme.specialColors.WHITE_TEXT,
    fontSize: 14,
    fontWeight: 400,
  },
  actionWrapper: {
    padding: '28px 16px',
    textAlign: 'center',
  },
  upgradeBtn: {
    borderRadius: '100px',
    fontSize: 16,
    fontWeight: 700,
  },
  codeWrapper: {
    marginTop: 14,
    display: 'flex',
    justifyContent: 'center',
  },
  codeLabel: {
    padding: '2px 5px',
    fontWeight: 600,
    color: theme.palette.text.secondary,
    // backgroundColor: theme.palette.background.default,
    borderRadius: '3px 0 0 3px',
  },
  couponCode: {
    padding: '2px 8px',
    color: theme.palette.text.primary,
    fontWeight: 600,
    backgroundColor: theme.specialColors.BLUE_HIGHLIGHT,
    borderRadius: '0 3px 3px 0',
    textTransform: 'uppercase',
  },
  footer: {
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.background.default,
    fontSize: 14,
    fontWeight: 600,
    textAlign: 'center',
    padding: '7px 16px 11px',
  },
  closeBtn: {
    color: theme.specialColors.WHITE_TEXT,
    position: 'absolute',
    right: 0,
    top: 0,
    '&:hover': {
      color: theme.specialColors.WHITE_TEXT,
    },
  },
}));

function SaleDialog() {
  const classes = useStyles();
  const router = useRouter();
  const isAuthenticated = useSelector((state: RootState) => state?.user?.isAuthenticated);
  const isExtension = useSelector((state: RootState) => state.common.platform.isExtension) || false;
  const isNative = useSelector((state: RootState) => state.common.platform.native);
  const isPremium = useSelector((rootState: RootState) => rootState?.user?.premium);
  const independentTool = useSelector((rootState: RootState) => rootState?.common.independentTool);
  const [saleOpen, setSaleOpen] = useState(false);

  const saleRoutes = ['/'];

  const showSaleDialog = () =>
    saleRoutes.includes(router.pathname) &&
    isAuthenticated &&
    !isExtension &&
    !isNative &&
    !isPremium &&
    !independentTool &&
    !(QuillLocalStorage.getInstance().get('hidePremiumUpsellDialog', 'false') === 'true');

  const handleClose = () => {
    setSaleOpen(false);
    QuillLocalStorage.getInstance().set('hidePremiumUpsellDialog', 'true');
    QuillLocalStorage.getInstance().delete('hideSaleDialog');
  };

  useEffect(() => {
    async function init() {
      const { getDatabase, onValue, ref } = await firebase.database;

      onValue(ref(getDatabase(), 'modalConfig'), (snapshot: { val: Function }) => {
        try {
          const data = snapshot.val();
          const modalConfig = { ...data };
          if (modalConfig.premiumModesSaleVisible === true) {
            setSaleOpen(showSaleDialog() || false);
          }
        } catch (e) {
          logger('FirebaseModalConfig', e);
        }
      });
    }

    init();
  }, [router.pathname]);

  return (
    <div>
      <Dialog
        maxWidth="xs"
        open={saleOpen}
        onClose={(event, reason) => {
          if (reason !== 'backdropClick') {
            handleClose();
            Track.modalStateChangeAction('closed', 'comn/user/interacted_premium_mode_upsell_modal');
          }
        }}
        classes={{ paper: classes.dialogPaper }}>
        <DialogContent className={classes.dialogContent}>
          <IconButton
            className={classes.closeBtn}
            onClick={() => {
              handleClose();
              Track.modalStateChangeAction('closed', 'comn/user/interacted_premium_mode_upsell_modal');
            }}>
            <Close />
          </IconButton>
          <div className={classes.discountWrapper}>
            <div className={classes.topText}>Write better and faster with QuillBot’s Premium Modes.</div>
            <Img src="/images/theme/commonImages/discount25.svg" alt="Sale Coupon" className={classes.couponImg} />
            <div className={classes.discountLabel}>Annual memberships</div>
          </div>
          <div className={classes.actionWrapper}>
            <Button
              variant="contained"
              color="primary"
              className={classes.upgradeBtn}
              onClick={() => {
                Track.modalStateChangeAction('accepted', 'comn/user/interacted_premium_mode_upsell_modal');
                window.open('https://quillbot.com/upgrade?p=12&coupon=MODES25&referrer=premiumModesUpsellModal');
                handleClose();
              }}>
              Upgrade Now
            </Button>
            <div className={classes.codeWrapper}>
              <div className={classes.codeLabel}>Code:</div>
              <div className={classes.couponCode}>MODES25</div>
            </div>
          </div>
          <div className={classes.footer}>
            Sale ends on May 31<sup>st</sup>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default SaleDialog;
